import React, { useCallback } from 'react';
import { ReactComponent as SvgMarker } from 'public/assets/icons/marker.svg';
import { ReactComponent as SvgDeal } from 'public/assets/icons/deal.svg';
import { cn } from '@/util/cn';

import { ISkiResortShop } from '@/api/resort/types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { useAnalytics } from '@/tracking/analytics';

import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useDistanceFormatter } from '@/i18n/unitFormat';
import ARLogoRed from './Primary_Logo_Mono_Red.png';

// Partner ski-rental offer card. One shape:
//   - 4:3 cover photo with the distance-from-resort pill bottom-left
//     and the Alpy.com partner mark top-right (contractual attribution)
//   - shop name + town/street below the photo
//   - discount + "View deal" CTA in a muted footer, bottom-aligned
//
// The card carries NO width of its own — the consuming grid owns the
// columns (see `page-components/resort/skirentals.tsx`). `h-full` plus
// `mt-auto` on the footer keeps every card in a row the same height with
// its CTA on the same baseline.

export interface IProps {
  shop: ISkiResortShop;
  className?: string;
}

// Bottom-left overlay pill. Same treatment as the timestamp pill on
// `common/ResortWebcam` — the brand gradient this used to carry left the
// design system when the semantic tokens landed.
const pinClass = [
  'absolute bottom-3 left-3 inline-flex items-center gap-1',
  'rounded bg-black/65 px-2 py-0.5 text-xs font-medium text-white',
].join(' ');

// Partner mark sits top-right so it never competes with the distance
// pill for space on a narrow card.
const partnerMarkClass = [
  'absolute right-2 top-2 h-6 w-auto',
  'rounded bg-white/95 px-1.5 py-1 shadow-sm',
].join(' ');

export const SkiRentalShop = ({
  className,
  shop,
}: IProps) => {
  const { t } = useOTSTranslation('common');
  const address = [shop.town, shop.street].filter((s) => !!s).join(', ');
  const {
    linkToShop, discount, image, thumbnail, title, distance,
  } = shop;

  const dt = useDistanceFormatter('kmOrMile');

  const a = useAnalytics();
  const shopId = shop.id || shop.uuid;

  const onClick = useCallback(() => {
    a.skiRentalViewDeal(shopId);
  }, [a, shopId]);

  const photo = image || thumbnail;
  const discountLabel = discount
    ? `−${(discount * 100).toFixed(0)}% ${t('resort.skiRental.off')}`
    : '';

  return (
    <Card className={cn('flex h-full flex-col overflow-hidden', className)}>
      {/* eslint-disable-next-line react/jsx-no-target-blank */}
      <a
        className="relative block aspect-[4/3] w-full overflow-hidden bg-muted"
        href={linkToShop}
        target="_blank"
        rel="nofollow"
        onClick={onClick}
      >
        {photo && (
          <img
            src={photo}
            alt={title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        )}
        {!!distance && (
          <span className={pinClass}>
            <SvgMarker className="size-3 fill-current" aria-hidden />
            {dt(distance / 1000)}
          </span>
        )}
        <img className={partnerMarkClass} src={ARLogoRed.src} alt="Alpy.com" />
      </a>
      <div className="flex flex-1 flex-col p-3">
        <div className="mb-3">
          <Heading level={3} size="base" className="mb-0.5">
            {title}
          </Heading>
          {!!address && <Text variant="subtle">{address}</Text>}
        </div>
        <div
          className={cn(
            'mt-auto flex items-center gap-3 rounded-md bg-muted p-2.5',
            discountLabel ? 'justify-between' : 'justify-end',
          )}
        >
          {!!discountLabel && (
            <span className="text-sm font-extrabold leading-tight text-primary">
              {discountLabel}
            </span>
          )}
          <Button asChild size="sm" className="shrink-0 px-2.5 text-xs">
            {/* eslint-disable-next-line react/jsx-no-target-blank */}
            <a href={linkToShop} target="_blank" rel="nofollow" onClick={onClick}>
              <SvgDeal className="fill-current" />
              <span>{t('resort.lodging.viewDeal')}</span>
            </a>
          </Button>
        </div>
      </div>
    </Card>
  );
};
